import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {Log} from 'ng2-logger';
import {UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {Store} from "@ngrx/store";
import {AppUtils} from "@app/shared/util/app.utils";
import {StrainMeasurementImage} from "@app/strain/common/models/view-measurement-models/strain-measurement.image";

@Component({
    selector: 'app-strain-measurement-images-form',
    templateUrl: './strain-measurement-images-form.component.html',
    styleUrls: ['./strain-measurement-images-form.component.scss']
})
export class StrainMeasurementImagesFormComponent implements OnInit, OnChanges {
    _logger = Log.create(StrainMeasurementImagesFormComponent.name);

    @Input() showValidations = false;
    @Input() isLoading = false;
    @Input() isEditMode: boolean;
    @Input() measurementImages: StrainMeasurementImage[];

    public form: UntypedFormGroup;

    isSelected = true;


    constructor(private store: Store<any>,
                private fb: UntypedFormBuilder) {
    }

    ngOnInit() {
        this._logger.info("ngOnInit:: Preparing strain configuration form");

        this.initForm(this.isEditMode);
    }

    control(name: string): UntypedFormControl {
        return this.form.get(name) as UntypedFormControl;
    }

    array(name: string) {
        return this.form.get(name) as UntypedFormArray;
    }

    arrayControl(index, name) {
        return (this.array('images').controls[index] as UntypedFormArray).get(name) as UntypedFormControl;
    }

    addItem() {
        this.array('images').push(this.buildRow());
        this.showValidations = false;
    }

    removeItem(index) {
        this.array('images').removeAt(index);
    }

    private buildRow(item = null): UntypedFormGroup {
        if (!item) {
            return this.fb.group({
                frontSideImage: [null, Validators.required],
                backSideImage: [null, Validators.required],
                observations: [null, Validators.required],
            });
        } else {
            return this.fb.group({
                frontSideImage: [item.frontSideImage && item.frontSideImage.data ?
                    this.getFile(item.frontSideImage) : [], Validators.required],
                backSideImage: [item.backSideImage && item.backSideImage.data ?
                    this.getFile(item.backSideImage) : [], Validators.required],
                observations: [item.observations, Validators.required],
            });
        }
    }

    value() {
        if (this.form && this.form.value && this.form.value.images) {
            const images = this.form.value.images;
            for (let i = 0; i < images.length; i++) {
                images[i].backSideImage = this.convertToFile(images[i].backSideImage, 'backSideImage.jpg');
                images[i].frontSideImage = this.convertToFile(images[i].frontSideImage, 'frontSideImage.jpg');
            }
        }
        return this.form.value;
    }

    valid() {
        return this.form && this.form.valid;
    }

    reset() {
        this._logger.info("resetForm:: Resetting line details form");
        this.form.reset();
    }

    initForm(isEditMode) {
        this.showValidations = false;

        this.form = this.fb.group({
            id: [null],
            images: this.fb.array([], Validators.required)
        });

        if (isEditMode && this.measurementImages && this.measurementImages.length > 0) {
            this.measurementImages.forEach(item => {
                this.array('images').push(this.buildRow(item))
            });
        } else {
            this.addItem();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
    }

    onDuplicate(index: any) {
        const newRow = this.buildRow({
            frontSideImage: this.array('images').controls[index].get('frontSideImage').value,
            backSideImage: this.array('images').controls[index].get('backSideImage').value,
            observations: this.array('images').controls[index].get('observations').value,
        });

        this.array('images').push(newRow);
    }

    getFrontSideImage(index: any) {
        return this.isEditMode && this.measurementImages && this.measurementImages[index] ?
            this.measurementImages[index].frontSideImage.url : '';
    }

    getBackSideImage(index) {
        return this.isEditMode && this.measurementImages && this.measurementImages[index] ?
            this.measurementImages[index].backSideImage.url : '';
    }

    convertToFile(imageForm, imageName) {
        if (imageForm instanceof File) {
            return imageForm;
        }

        if (imageForm && imageForm.indexOf('base64') && imageForm.indexOf('base64') !== -1) {
            return new File([AppUtils.dataURItoBlob(imageForm)], imageName, {type: 'image/jpeg'});
        }
    }

    getFile(item) {
        const blob = AppUtils.buildBlobFromDataString(item.data);
        return new File([blob], item.name, {type: item.contentType});
    }
}
